/**
 * AuditDiff — field-by-field diff view for an audit detail entry.
 *
 * - Update: renders only keys that actually differ (prefers changedFields
 *   ordering from the backend; unions in any additional diffs detected).
 * - Create: renders all after-keys as "Neu".
 * - Delete: renders all before-keys as "Entfernt".
 * - Nested objects / arrays: placeholder row with a per-field "Rohdaten"
 *   disclosure so the diff doesn't turn into a wall of JSON.
 * - Raw-JSON toggle at the bottom reveals the full before/after dumps as
 *   an escape hatch for debugging.
 */
import { useMemo, useState } from 'react';
import type { AuditAction } from '@armard/shared';

interface AuditDiffProps {
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  changedFields: string[] | null;
  action: AuditAction;
}

type DiffKind = 'changed' | 'added' | 'removed';

interface DiffRow {
  key: string;
  kind: DiffKind;
  before: unknown;
  after: unknown;
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function shallowEqual(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (typeof a !== typeof b) return false;
  if (a === null || b === null) return false;
  if (Array.isArray(a) && Array.isArray(b)) {
    return JSON.stringify(a) === JSON.stringify(b);
  }
  if (isPlainObject(a) && isPlainObject(b)) {
    return JSON.stringify(a) === JSON.stringify(b);
  }
  return false;
}

function buildRows(
  before: Record<string, unknown> | null,
  after: Record<string, unknown> | null,
  changedFields: string[] | null,
  action: AuditAction
): DiffRow[] {
  if (action === 'create') {
    const src = after ?? {};
    return Object.keys(src).map((key) => ({
      key,
      kind: 'added' as const,
      before: undefined,
      after: src[key],
    }));
  }
  if (action === 'delete') {
    const src = before ?? {};
    return Object.keys(src).map((key) => ({
      key,
      kind: 'removed' as const,
      before: src[key],
      after: undefined,
    }));
  }

  // update
  const b = before ?? {};
  const a = after ?? {};
  const union = new Set<string>([...Object.keys(b), ...Object.keys(a)]);
  const differing: string[] = [];
  for (const key of union) {
    if (!shallowEqual(b[key], a[key])) differing.push(key);
  }

  // Prefer backend-provided ordering when present; append any extras we
  // detected but the backend didn't list.
  const ordered: string[] = [];
  const seen = new Set<string>();
  if (changedFields) {
    for (const key of changedFields) {
      if (differing.includes(key) && !seen.has(key)) {
        ordered.push(key);
        seen.add(key);
      }
    }
  }
  for (const key of differing) {
    if (!seen.has(key)) {
      ordered.push(key);
      seen.add(key);
    }
  }

  return ordered.map((key) => {
    const hasBefore = Object.prototype.hasOwnProperty.call(b, key);
    const hasAfter = Object.prototype.hasOwnProperty.call(a, key);
    let kind: DiffKind = 'changed';
    if (!hasBefore && hasAfter) kind = 'added';
    else if (hasBefore && !hasAfter) kind = 'removed';
    return { key, kind, before: b[key], after: a[key] };
  });
}

function renderPrimitive(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'boolean') return value ? 'Ja' : 'Nein';
  if (typeof value === 'string' && value === '') return '"" (leer)';
  return String(value);
}

function isComplex(value: unknown): boolean {
  return Array.isArray(value) || isPlainObject(value);
}

function complexLabel(value: unknown): string {
  if (Array.isArray(value)) {
    return `Liste (${value.length} Einträge)`;
  }
  if (isPlainObject(value)) {
    return `Objekt (${Object.keys(value).length} Felder)`;
  }
  return '—';
}

function DiffValue({ value, tone }: { value: unknown; tone: 'before' | 'after' }) {
  const [showRaw, setShowRaw] = useState(false);
  const toneClass = tone === 'before' ? 'text-red-300/80 line-through' : 'text-green-300';

  if (isComplex(value)) {
    return (
      <span className="inline-flex flex-col gap-1 align-top">
        <span className="inline-flex items-center gap-2">
          <span className={`${toneClass} italic`}>{complexLabel(value)}</span>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setShowRaw((v) => !v);
            }}
            className="text-[10px] text-white/40 hover:text-white/70 transition-colors underline decoration-dotted"
          >
            {showRaw ? 'ausblenden' : 'Rohdaten'}
          </button>
        </span>
        {showRaw && (
          <pre className="mt-1 text-[11px] text-white/70 bg-black/30 rounded p-2 border border-white/5 overflow-x-auto">
            {JSON.stringify(value, null, 2)}
          </pre>
        )}
      </span>
    );
  }

  return <span className={toneClass}>{renderPrimitive(value)}</span>;
}

function DiffRowView({ row }: { row: DiffRow }) {
  return (
    <div className="grid grid-cols-[minmax(120px,auto)_1fr] gap-x-3 gap-y-0.5 text-xs py-1.5 border-b border-white/[0.04] last:border-b-0">
      <div className="font-mono text-white/50 pt-px">{row.key}</div>
      <div className="flex items-start gap-2 flex-wrap">
        {row.kind === 'changed' && (
          <>
            <DiffValue value={row.before} tone="before" />
            <span className="text-white/30">→</span>
            <DiffValue value={row.after} tone="after" />
          </>
        )}
        {row.kind === 'added' && (
          <>
            <span className="text-[10px] uppercase tracking-wider text-green-400/60">Neu</span>
            <DiffValue value={row.after} tone="after" />
          </>
        )}
        {row.kind === 'removed' && (
          <>
            <span className="text-[10px] uppercase tracking-wider text-red-400/60">Entfernt</span>
            <DiffValue value={row.before} tone="before" />
          </>
        )}
      </div>
    </div>
  );
}

export function AuditDiff({ before, after, changedFields, action }: AuditDiffProps) {
  const rows = useMemo(
    () => buildRows(before, after, changedFields, action),
    [before, after, changedFields, action]
  );
  const [activeField, setActiveField] = useState<string | null>(null);
  const [showRaw, setShowRaw] = useState(false);

  if (rows.length === 0) {
    return (
      <div className="space-y-3">
        <p className="text-xs text-white/40">Keine Felder geändert.</p>
        <RawToggle showRaw={showRaw} onToggle={() => setShowRaw((v) => !v)} />
        {showRaw && <RawBlocks before={before} after={after} />}
      </div>
    );
  }

  const visibleRows = activeField ? rows.filter((r) => r.key === activeField) : rows;
  const showChips = rows.length >= 2;

  return (
    <div className="space-y-3">
      {showChips && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[10px] uppercase tracking-wider text-white/30 mr-1">
            Felder
          </span>
          {rows.map((r) => {
            const active = activeField === r.key;
            return (
              <button
                key={r.key}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveField(active ? null : r.key);
                }}
                className={`
                  px-1.5 py-0.5 rounded text-[10px] font-mono transition-colors
                  ${active
                    ? 'bg-white/15 text-white ring-1 ring-white/20'
                    : 'bg-white/5 text-white/50 hover:bg-white/10 hover:text-white/80'}
                `}
              >
                {r.key}
              </button>
            );
          })}
          {activeField && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setActiveField(null);
              }}
              className="ml-1 text-[10px] text-white/40 hover:text-white/70 transition-colors"
            >
              alle zeigen
            </button>
          )}
        </div>
      )}

      <div>
        {visibleRows.map((row) => (
          <DiffRowView key={row.key} row={row} />
        ))}
      </div>

      <RawToggle showRaw={showRaw} onToggle={() => setShowRaw((v) => !v)} />
      {showRaw && <RawBlocks before={before} after={after} />}
    </div>
  );
}

function RawToggle({ showRaw, onToggle }: { showRaw: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
      className="text-[10px] text-white/30 hover:text-white/60 transition-colors underline decoration-dotted"
    >
      {showRaw ? 'Rohdaten ausblenden' : 'Rohdaten anzeigen'}
    </button>
  );
}

function RawBlocks({
  before,
  after,
}: {
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
}) {
  const hasBefore = before && Object.keys(before).length > 0;
  const hasAfter = after && Object.keys(after).length > 0;
  return (
    <div className="space-y-3 pt-2 border-t border-white/5">
      {hasBefore && (
        <div>
          <h4 className="text-[10px] uppercase tracking-wider text-white/40 mb-1.5">Vorher (Rohdaten)</h4>
          <pre className="text-[11px] text-white/70 overflow-x-auto bg-black/30 rounded p-2 border border-white/5">
            {JSON.stringify(before, null, 2)}
          </pre>
        </div>
      )}
      {hasAfter && (
        <div>
          <h4 className="text-[10px] uppercase tracking-wider text-white/40 mb-1.5">Nachher (Rohdaten)</h4>
          <pre className="text-[11px] text-white/70 overflow-x-auto bg-black/30 rounded p-2 border border-white/5">
            {JSON.stringify(after, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}
